import { YAxisProps, YAxisTickProps } from './types';

type TickFormatterFn<V> = YAxisProps<unknown, V>['tickFormatter'];

type BuildTickFormatterParams<V> = Pick<
  YAxisProps<unknown, V>,
  'unitFormatter' | 'tickFormatter'
> &
  Pick<YAxisTickProps<V>, 'index' | 'visibleTicksCount'>;

/**
 * Builds a wrapped tick formatter that chains tickFormatter → unitFormatter.
 * When `isLast` is true and unitFormatter is provided, the unit suffix is appended.
 */
export const buildTickFormatter = <V>({
  unitFormatter,
  tickFormatter,
  index,
  visibleTicksCount,
}: BuildTickFormatterParams<V>): TickFormatterFn<V> | undefined => {
  const isLast =
    unitFormatter != null &&
    index != null &&
    visibleTicksCount != null &&
    index + 1 === visibleTicksCount;

  const formatter: TickFormatterFn<V> | undefined = tickFormatter
    ? (value, i): string => tickFormatter(value ?? ('' as V), i)
    : undefined;

  return isLast && unitFormatter
    ? (value, i): string => {
        const valueOrEmpty = value ?? ('' as V);
        const formattedValue = formatter?.(value, i) ?? String(valueOrEmpty);
        return unitFormatter(formattedValue, {
          value: valueOrEmpty,
          index: i,
        });
      }
    : formatter;
};
